/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useState } from "react";
import { Menu, Image } from "antd";
import { useNavigate, useLocation } from "react-router-dom";
import Logo from "../../assets/images/logo.png";
import LogoFull from "../../assets/images/auth-logo.png";
import DashboardIcon from "../../assets/icons/favorite-chart.svg";
import DashboardActiveIcon from "../../assets/icons/favorite-chart-active.svg";
import DealsIcon from "../../assets/icons/deals-icon.svg";
import DealsActiveIcon from "../../assets/icons/deals-active-icon.svg";
import SettlementIcon from "../../assets/icons/settlement-icon.svg";
import SettlementActiveIcon from "../../assets/icons/settlement-active-icon.svg";
import ReportsIcon from "../../assets/icons/reports-icon.svg";
import ReportsActiveIcon from "../../assets/icons/reports-active-icon.svg";
import WalletIcon from "../../assets/icons/wallet-icon.svg";
import WalletActiveIcon from "../../assets/icons/wallet-active-icon.svg";
import SupportIcon from "../../assets/icons/support-icon.svg";
import SupportActiveIcon from "../../assets/icons/support-active-icon.svg";
import QuestionnaireIcon from "../../assets/icons/questionnaire-icon.svg";
import QuestionnaireActiveIcon from "../../assets/icons/questionnaire-active-icon.svg";
import DealMngtIcon from "../../assets/icons/deal-mngt-icon.svg";
import DealMngtActiveIcon from "../../assets/icons/deal-mngt-active-icon.svg";
import CreditLimitIcon from "../../assets/icons/credit-limit-icon.svg";
import CreditLimitActiveIcon from "../../assets/icons/credit-limit-active-icon.svg";
import SupplierIcon from "../../assets/icons/supplier-icon.svg";
import SupplierActiveIcon from "../../assets/icons/supplier-active-icon.svg";
import { useSelector, useDispatch } from "react-redux";
import "./Sidebar.scss";
import { count } from "../../redux/slices/AuthSlice";
import {
  DEAL_CATEGORY_IDS,
  isInvoiceStyleCategory,
  isSupplyChainCategory,
  isVenturesCategory,
} from "../../helpers/dealCategoryProducts";

const SidebarContent = ({ collapsed, onClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const [selectedKey, setSelectedKey] = useState(location.pathname);
  const [isSmallScreen, setIsSmallScreen] = useState(false);
  const { onboardingStepStatus } = useSelector((state) => state.questionnaire);
  const { activeCategory } = useSelector((state) => state.dealCategory);
  const { user, countData } = useSelector((state) => state.auth);

  useEffect(() => {
    const handleResize = () => {
      setIsSmallScreen(window.innerWidth <= 768); // True for small screens
    };

    handleResize(); // Set initial value on component mount
    window.addEventListener("resize", handleResize); // Listen to window resize

    return () => {
      window.removeEventListener("resize", handleResize); // Clean up on unmount
    };
  }, []);

  const currentDealCategoryId =
    activeCategory?.dealCategoryId || user?.dealCategories?.[0]?.dealCategoryId;
  const isSupplyChain = isSupplyChainCategory(currentDealCategoryId);

  useEffect(() => {
    if (currentDealCategoryId) {
      dispatch(count({ type: "ALL", dealCategoryId: currentDealCategoryId }));
    }
  }, [dispatch, currentDealCategoryId]);

  const icon = (menuItemKey, activeIcon, inactiveIcon) => {
    return (
      <Image
        src={selectedKey === menuItemKey ? activeIcon : inactiveIcon}
        preview={false}
      />
    );
  };

  const idMenuItems = [
    {
      key: "/dashboard",
      icon: icon("/dashboard", DashboardActiveIcon, DashboardIcon),
      label: "Dashboard",
    },
    {
      key: "/questionnaire",
      icon: icon("/questionnaire", QuestionnaireActiveIcon, QuestionnaireIcon),
      label: "Questionnaire",
    },
    ...(isSupplyChain
      ? [
          {
            key: "/suppliers",
            icon: icon("/suppliers", SupplierActiveIcon, SupplierIcon),
            label:
              countData?.suppliers !== undefined
                ? `Supplier (${countData.suppliers})`
                : "Supplier",
          },
        ]
      : []),
    {
      key: "/credit-limit",
      icon: icon("/credit-limit", CreditLimitActiveIcon, CreditLimitIcon),
      label: "Credit Limit",
    },
    {
      key: "/deals",
      icon: icon("/deals", DealsActiveIcon, DealsIcon),
      label: `Deals (${countData?.deals || 0})`,
    },
    {
      key: "/settlement",
      icon: icon("/settlement", SettlementActiveIcon, SettlementIcon),
      label: "Settlement",
    },
    {
      key: "/reports",
      icon: icon("/reports", ReportsActiveIcon, ReportsIcon),
      label: "Reports",
    },
    {
      key: "/wallet",
      icon: icon("/wallet", WalletActiveIcon, WalletIcon),
      label: "Wallet",
    },
    // Conditionally render "Support" only on small screens
    ...(isSmallScreen
      ? [
          {
            key: "/support",
            icon: icon("/support", SupportActiveIcon, SupportIcon),
            label: "Support",
          },
        ]
      : []),
  ];

  const jvMenuItems = [
    {
      key: "/questionnaire",
      icon: icon("/questionnaire", QuestionnaireActiveIcon, QuestionnaireIcon),
      label: "Questionnaire",
    },
    {
      key: "/deal-management",
      icon: icon("/deal-management", DealMngtActiveIcon, DealMngtIcon),
      label: `Deals (${countData?.deals || 0})`,
    },
    // Conditionally render "Support" only on small screens
    ...(isSmallScreen
      ? [
          {
            key: "/support",
            icon: icon("/support", SupportActiveIcon, SupportIcon),
            label: "Support",
          },
        ]
      : []),
  ];

  const currentStatusId =
    activeCategory?.statusId || user?.dealCategories?.[0]?.statusId;
  const isActive = currentStatusId === "USM1000001";

  let baseMenu = [];

  if (
    isInvoiceStyleCategory(currentDealCategoryId) ||
    currentDealCategoryId === DEAL_CATEGORY_IDS.SUPPLY_CHAIN_FINANCE ||
    currentDealCategoryId === DEAL_CATEGORY_IDS.INVOICE_FINANCING
  ) {
    baseMenu = idMenuItems;
  } else if (
    isVenturesCategory(currentDealCategoryId) ||
    currentDealCategoryId === DEAL_CATEGORY_IDS.JOINT_VENTURES
  ) {
    baseMenu = jvMenuItems;
  }

  const menuItems = onboardingStepStatus
    ? isActive
      ? baseMenu
      : baseMenu.filter((item) => item.key === "/questionnaire")
    : [];

  const getSelectedKey = () => {
    if (location.pathname.startsWith("/suppliers") || location.pathname.startsWith("/supplier")) {
      return "/suppliers";
    }
    if (
      location.pathname === "/upload-supply-chain-files" ||
      location.pathname === "/supply-chain-upload" ||
      location.pathname === "/upload-inv-files" ||
      location.pathname === "/invoice-upload"
    ) {
      return "/deals";
    }
    if (location.pathname === "/deal-request") {
      return "/deal-management";
    }
    return location.pathname;
  };

  useEffect(() => {
    setSelectedKey(getSelectedKey());
  }, [location.pathname]);

  const handleMenuItemClick = (e) => {
    navigate(e.key);
    onClose();
  };

  return (
    <>
      <div className="logo-container">
        {collapsed ? (
          <Image src={Logo} className="logo" preview={false} />
        ) : (
          <Image src={LogoFull} className="logo-full" preview={false} />
        )}
      </div>
      <Menu
        mode="inline"
        selectedKeys={[selectedKey]}
        className="custom-menu"
        onClick={handleMenuItemClick}
        items={menuItems.map((item) => ({
          key: item.key,
          icon: item.icon,
          label: !collapsed ? item.label : "",
        }))}
      />
    </>
  );
};

export default SidebarContent;
